import React, { memo, useState } from 'react';
import Link from 'next/link';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';

import { Container, StyledList, StyledListItem } from '../../../styles';
import { NavbarButtonMenu } from '../../navbar-button-menu';
import { Stack, Typography, useTheme } from '@mui/material';
import { Button } from '@core/design-system/button';

interface NavbarPopoverProps {
  title: string;
  items: {
    label?: string;
    link?: string;
    description?: string;
    subTitle?: string;
    linkLabel?: string;
    subItems?: {
      label: string;
      link?: string;
    }[];
  }[];
}

export const NavbarProductPopover = memo<NavbarPopoverProps>(
  ({ title, items }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const theme = useTheme();

    const handleClose = () => {
      setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    return (
      <Container displayPrint="none">
        <NavbarButtonMenu
          id={id}
          isOpen={open}
          onClick={handleClick}
          title={title}
        />

        <Popover
          id={id}
          anchorEl={anchorEl}
          onClose={handleClose}
          open={open}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          sx={(theme) => ({
            '& .MuiPopover-paper': {
              backgroundColor: theme.palette.primary.main,
            },
          })}
        >
          <Stack direction="row" gap={5} sx={{ padding: '3rem' }}>
            {items.map((item, index) => (
              <Stack
                key={`navbar-${index}`}
                maxWidth="320px"
                sx={{
                  '&:last-child': {
                    borderLeft: `1px solid ${theme.palette.mediumNeutral.main}`,
                    paddingLeft: '3rem',
                  },
                }}
              >
                <Typography
                  variant="caption"
                  color={theme.palette.mediumNeutral.main}
                  fontWeight={600}
                  textTransform="uppercase"
                  lineHeight={1.6}
                  letterSpacing={0.4}
                >
                  {item?.subTitle}
                </Typography>
                <Typography
                  variant="h3"
                  component="p"
                  color={theme.palette.secondary.main}
                  fontWeight={600}
                  lineHeight={1.6}
                  letterSpacing={0.4}
                >
                  {item?.label}
                </Typography>
                <Typography
                  variant="caption"
                  color={theme.palette.primary.contrastText}
                  fontWeight={400}
                  lineHeight={1.6}
                  letterSpacing={0.4}
                >
                  {item?.description}
                </Typography>

                <StyledList>
                  {item?.subItems?.map((subitem, index) => (
                    <StyledListItem key={subitem.label} color={'white'}>
                      <Link
                        href={subitem?.link}
                        prefetch={false}
                        target="_blank"
                        rel="noopener"
                        style={{ padding: 0 }}
                      >
                        <ListItemText
                          sx={{
                            '& span': {
                              fontSize: theme.typography.body2.fontSize,
                              fontWeight: 600,
                            },
                          }}
                        >
                          {subitem?.label}
                        </ListItemText>
                      </Link>
                    </StyledListItem>
                  ))}
                </StyledList>

                {item?.link && (
                  <Button
                    target="_blank"
                    variant="outlined"
                    color="secondary"
                    fullWidth
                    href={item?.link}
                    component="a"
                    {...(!item?.link?.includes('http') && {
                      LinkComponent: Link,
                    })}
                    sx={{
                      mt: 1,
                      textTransform: 'none',
                      color: theme.palette.primary.contrastText,
                    }}
                  >
                    {item?.linkLabel}
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </Popover>
      </Container>
    );
  }
);
